import React, { useEffect, useState } from 'react';
import { FileText, AlertCircle, ClipboardList, Upload, Users, Search, Clock } from 'lucide-react';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { H1, H2, H3 } from '../../components/typography';
import EmailTest from '../../components/EmailTest';
import DebugAssociations from '../../components/DebugAssociations';
import FixAssociations from '../../components/FixAssociations';

export default function HyriaOverview() {
  const [applications, setApplications] = useState([]);
  const [auditOrderStats, setAuditOrderStats] = useState({ pending: 0, completed: 0 });
  const [expiringRentals, setExpiringRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchApplicationsData();
      } else {
        setError('Inte inloggad');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchApplicationsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const ref = collection(db, 'applications');
      const snap = await getDocs(ref);
      const allApplications = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApplications(allApplications);

      // Hämta avvikelserapport-statistik
      try {
        const auditSnap = await getDocs(collection(db, 'auditReportOrders'));
        const pending = auditSnap.docs.filter(d => d.data().status === 'pending').length;
        const completed = auditSnap.docs.filter(d => d.data().status === 'completed').length;
        setAuditOrderStats({ pending, completed });
      } catch (_) { /* collection might not exist */ }

      // Beräkna uthyrningar som löper ut inom 90 dagar
      try {
        const assocSnap = await getDocs(collection(db, 'associations'));
        const assocMap = {};
        assocSnap.docs.forEach(d => { assocMap[d.id] = d.data().name || 'Okänd förening'; });

        const today = new Date();
        const ninetyDaysFromNow = new Date();
        ninetyDaysFromNow.setDate(today.getDate() + 90);

        const expiring = allApplications
          .filter(app => {
            const isActive = ['FINAL_APPROVED', 'ACTIVE', 'CONTRACT_SIGNED'].includes(app.status);
            if (!isActive) return false;
            const endDateStr = app.contractEndDate || app.toDate;
            if (!endDateStr) return false;
            const endDate = new Date(endDateStr);
            if (isNaN(endDate.getTime())) return false;
            return endDate >= today && endDate <= ninetyDaysFromNow;
          })
          .map(app => ({
            ...app,
            associationName: assocMap[app.associationId] || 'Okänd förening',
          }))
          .sort((a, b) => {
            const assocCmp = a.associationName.localeCompare(b.associationName, 'sv');
            if (assocCmp !== 0) return assocCmp;
            return (a.apartmentNumber || '').localeCompare(b.apartmentNumber || '', 'sv', { numeric: true });
          });

        setExpiringRentals(expiring);
      } catch (_) { /* fortsätt utan denna sektion */ }
    } catch (err) {
      setError('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  };

  // KPI calculations for Hyria perspective
  const pendingCreditChecks = applications.filter(a => a.status === 'TENANT_INFO_SUBMITTED').length;
  const completedCreditChecks = applications.filter(a => a.status === 'CREDIT_CHECK_COMPLETE').length;
  const totalApplications = applications.length;
  const averageProcessingTime = '2.3 dagar'; // Mockad data

  const kpis = [
    { 
      value: pendingCreditChecks, 
      label: 'Väntande kreditkontroller', 
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      onClick: () => navigate('/hyria-dashboard/credit-checks?filter=pending')
    },
    { 
      value: completedCreditChecks, 
      label: 'Avslutade kreditkontroller', 
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/hyria-dashboard/credit-checks?filter=completed')
    },
    { 
      value: totalApplications, 
      label: 'Totala ansökningar', 
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/hyria-dashboard/credit-checks')
    },
    { 
      value: averageProcessingTime, 
      label: 'Genomsnittlig handläggningstid', 
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      onClick: () => navigate('/hyria-dashboard/reports')
    },
  ];

  const recentApplications = applications
    .filter(a => a.status === 'TENANT_INFO_SUBMITTED')
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1 className="mb-6">Hyria Admin Dashboard</H1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {kpis.map((kpi, i) => (
          <div 
            key={i} 
            onClick={kpi.onClick}
            className={`${kpi.bgColor} rounded-2xl shadow p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 group`}
          >
            <div className={`text-2xl font-bold ${kpi.color} mb-1 group-hover:scale-110 transition-transform`}>
              {kpi.value}
            </div>
            <div className="text-sm text-zinc-600 font-medium">
              {kpi.label}
            </div>
            <div className="text-xs text-zinc-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Klicka för detaljer →
            </div>
          </div>
        ))}
      </div>

      {/* Avvikelserapporter */}
      {(auditOrderStats.pending > 0 || auditOrderStats.completed > 0) && (
        <div
          className="bg-white rounded-2xl shadow p-5 mb-8 cursor-pointer hover:shadow-lg transition-all flex items-center justify-between"
          onClick={() => navigate('/hyria-dashboard/associations')}
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#222222]">Avvikelserapporter</h3>
              <p className="text-sm text-zinc-500">
                {auditOrderStats.pending > 0 && <span className="text-amber-600 font-medium">{auditOrderStats.pending} att hantera</span>}
                {auditOrderStats.pending > 0 && auditOrderStats.completed > 0 && ' · '}
                {auditOrderStats.completed > 0 && <span>{auditOrderStats.completed} klara</span>}
              </p>
            </div>
          </div>
          {auditOrderStats.pending > 0 && (
            <span className="inline-flex items-center px-3 py-1 text-sm font-bold text-white bg-red-500 rounded-full">
              {auditOrderStats.pending}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Senaste ansökningar för kreditkontroll */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow p-6">
            <H2 className="mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Väntande kreditkontroller
            </H2>
            {recentApplications.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                Inga väntande kreditkontroller
              </div>
            ) : (
              <div className="space-y-3">
                {recentApplications.map(app => (
                  <div 
                    key={app.id}
                    onClick={() => navigate(`/hyria-dashboard/credit-checks?id=${app.id}`)}
                    className="p-4 border border-zinc-200 rounded-lg hover:bg-zinc-50 cursor-pointer transition"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-zinc-800">
                          {app.tenant || 'Hyresgäst ej angiven'}
                        </div>
                        <div className="text-sm text-zinc-500">
                          {app.selectedApartment || 'Lägenhet ej angiven'}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          Inkommen: {app.createdAt ? new Date(app.createdAt).toLocaleDateString('sv-SE') : 'Okänt datum'}
                        </div>
                      </div>
                      <div className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                        Väntar kreditkontroll
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <button 
                onClick={() => navigate('/hyria-dashboard/credit-checks')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Se alla kreditkontroller →
              </button>
            </div>
          </div>
        </div>

        {/* Snabbåtgärder */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow p-6">
            <H3 className="mb-4">Snabbåtgärder</H3>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/hyria-dashboard/credit-checks')}
                className="w-full flex items-center gap-3 p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition text-left"
              >
                <Upload className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="font-medium text-zinc-800">Hantera kreditkontroller</div>
                  <div className="text-xs text-zinc-500">Ladda upp PDF rapporter</div>
                </div>
              </button>
              
              <button 
                onClick={() => navigate('/hyria-dashboard/reports')}
                className="w-full flex items-center gap-3 p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition text-left"
              >
                <ClipboardList className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-medium text-zinc-800">Generera rapporter</div>
                  <div className="text-xs text-zinc-500">Månadsrapporter och statistik</div>
                </div>
              </button>

              <button 
                onClick={() => navigate('/hyria-dashboard/credit-checks')}
                className="w-full flex items-center gap-3 p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition text-left"
              >
                <Search className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="font-medium text-zinc-800">Sök ansökningar</div>
                  <div className="text-xs text-zinc-500">Hitta specifika ärenden</div>
                </div>
              </button>
            </div>
          </div>

          {/* Systemstatus */}
          <div className="bg-white rounded-2xl shadow p-6">
            <H3 className="mb-4">Systemstatus</H3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">API Status</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Online</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">Database</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Online</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">PDF Storage</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Debug & Test Sections - Endast för utveckling */}
        {process.env.NODE_ENV === 'development' && (
          <>
            <div className="mt-8">
              <H2 className="mb-4">🔍 Debug: Föreningar & Ansökningar</H2>
              <DebugAssociations />
            </div>

              <div className="mt-8">
                <H2 className="mb-4">🔧 Fix: Saknade AssociationId</H2>
                <FixAssociations />
              </div>
              
              <div className="mt-8">
                <H2 className="mb-4">🧪 Email Test (Development)</H2>
                <EmailTest />
              </div>
          </>
        )}
      </div>

      {/* Uthyrningar som löper ut inom 90 dagar */}
      <div className="bg-white rounded-2xl shadow p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <H2 className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Uthyrningar som löper ut inom 90 dagar
          </H2>
          <span className="text-sm text-zinc-500">
            {expiringRentals.length} st
          </span>
        </div>

        {expiringRentals.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            Inga uthyrningar löper ut inom 90 dagar
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Förening</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Adress</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Lägenhet</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Hyresgäst</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Slutdatum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {expiringRentals.map(app => {
                  const endDateStr = app.contractEndDate || app.toDate;
                  const endDate = new Date(endDateStr);
                  const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
                  const urgencyClass = daysLeft <= 30 ? 'text-red-600 font-semibold' :
                                       daysLeft <= 60 ? 'text-orange-600' : 'text-zinc-700';
                  return (
                    <tr
                      key={app.id}
                      className="hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/hyria-dashboard/applications?id=${app.id}`)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-zinc-900">{app.associationName}</td>
                      <td className="px-4 py-3 text-sm text-zinc-700">{app.address || '—'}</td>
                      <td className="px-4 py-3 text-sm text-zinc-700">{app.apartmentNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-zinc-700">
                        {`${app.tenantFirstName || ''} ${app.tenantLastName || ''}`.trim() || '—'}
                      </td>
                      <td className={`px-4 py-3 text-sm tabular-nums ${urgencyClass}`}>
                        {endDate.toLocaleDateString('sv-SE')}
                        <span className="text-xs text-zinc-400 ml-2">({daysLeft} dagar kvar)</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
